// icons.jsx — clean inline SVG weather icons + UI glyphs
// Ported from /tmp/handoff-fcc-weather/src/icons.jsx — same SVG geometry,
// converted from React.useMemo + window.WXI globals to Preact + ES exports.

import { useMemo } from 'preact/hooks';

const __wxIconCounter = { n: 0 };
const wxUid = () => `wx${++__wxIconCounter.n}`;

export function WxIcon({ kind, size = 40, mono = false, color = '#f5f6f7', accent = '#c7d2fe' }) {
  const u = useMemo(wxUid, []);
  const s = { width: size, height: size, display: 'inline-block', flexShrink: 0 };
  const sun     = mono ? color  : '#fbbf24';
  const sunGlow = mono ? color  : '#ffe58f';
  const moon    = mono ? color  : '#f1f5f9';
  const cloudHi = mono ? color  : '#e2e8f0';
  const cloudLo = mono ? color  : '#94a3b8';
  const drop    = mono ? color  : '#60a5fa';
  const flake   = mono ? color  : '#e0f2fe';
  const bolt    = mono ? accent : '#fbbf24';
  const fog     = mono ? color  : '#cbd5e1';

  switch (kind) {
    case 'clear':
      return (
        <svg viewBox="0 0 64 64" style={s} aria-hidden="true">
          <defs><radialGradient id={u+'sun'} cx="50%" cy="50%" r="55%"><stop offset="0%" stopColor={sunGlow}/><stop offset="100%" stopColor={sun}/></radialGradient></defs>
          <circle cx="32" cy="32" r="11" fill={`url(#${u}sun)`}/>
          <g stroke={sun} strokeWidth="2.6" strokeLinecap="round">
            <line x1="32" y1="6"  x2="32" y2="13"/><line x1="32" y1="51" x2="32" y2="58"/>
            <line x1="6"  y1="32" x2="13" y2="32"/><line x1="51" y1="32" x2="58" y2="32"/>
            <line x1="14" y1="14" x2="19" y2="19"/><line x1="45" y1="45" x2="50" y2="50"/>
            <line x1="14" y1="50" x2="19" y2="45"/><line x1="45" y1="19" x2="50" y2="14"/>
          </g>
        </svg>
      );
    case 'clear-night':
      return (
        <svg viewBox="0 0 64 64" style={s} aria-hidden="true">
          <path d="M44 36a16 16 0 11-16-22 13 13 0 0016 22z" fill={moon}/>
          <circle cx="22" cy="22" r="1.2" fill={moon} opacity=".6"/>
          <circle cx="48" cy="14" r="1" fill={moon} opacity=".5"/>
          <circle cx="14" cy="46" r=".8" fill={moon} opacity=".5"/>
        </svg>
      );
    case 'partly-cloudy':
      return (
        <svg viewBox="0 0 64 64" style={s} aria-hidden="true">
          <defs>
            <radialGradient id={u+'s'} cx="50%" cy="50%" r="55%"><stop offset="0%" stopColor={sunGlow}/><stop offset="100%" stopColor={sun}/></radialGradient>
            <linearGradient id={u+'c'} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={cloudHi}/><stop offset="1" stopColor={cloudLo}/></linearGradient>
          </defs>
          <circle cx="24" cy="24" r="9" fill={`url(#${u}s)`}/>
          <g fill={`url(#${u}c)`}>
            <circle cx="36" cy="38" r="9"/><circle cx="46" cy="36" r="7"/><circle cx="28" cy="40" r="6"/>
            <rect x="22" y="38" width="32" height="10" rx="5"/>
          </g>
        </svg>
      );
    case 'cloudy':
      return (
        <svg viewBox="0 0 64 64" style={s} aria-hidden="true">
          <defs><linearGradient id={u+'c'} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={cloudHi}/><stop offset="1" stopColor={cloudLo}/></linearGradient></defs>
          <g fill={`url(#${u}c)`}>
            <circle cx="22" cy="30" r="9"/><circle cx="34" cy="26" r="11"/><circle cx="44" cy="31" r="8"/>
            <rect x="14" y="30" width="38" height="14" rx="7"/>
          </g>
        </svg>
      );
    case 'rain':
    case 'drizzle':
      return (
        <svg viewBox="0 0 64 64" style={s} aria-hidden="true">
          <defs><linearGradient id={u+'c'} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={cloudHi}/><stop offset="1" stopColor={cloudLo}/></linearGradient></defs>
          <g fill={`url(#${u}c)`}>
            <circle cx="22" cy="26" r="8"/><circle cx="32" cy="22" r="10"/><circle cx="44" cy="26" r="7"/>
            <rect x="14" y="26" width="36" height="12" rx="6"/>
          </g>
          <g stroke={drop} strokeWidth="2.6" strokeLinecap="round">
            <line x1="22" y1="44" x2="19" y2="54"/>
            <line x1="32" y1="44" x2="29" y2="54"/>
            <line x1="42" y1="44" x2="39" y2="54"/>
          </g>
        </svg>
      );
    case 'snow':
      return (
        <svg viewBox="0 0 64 64" style={s} aria-hidden="true">
          <defs><linearGradient id={u+'c'} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={cloudHi}/><stop offset="1" stopColor={cloudLo}/></linearGradient></defs>
          <g fill={`url(#${u}c)`}>
            <circle cx="22" cy="26" r="8"/><circle cx="32" cy="22" r="10"/><circle cx="44" cy="26" r="7"/>
            <rect x="14" y="26" width="36" height="12" rx="6"/>
          </g>
          <g fill={flake}>
            <circle cx="22" cy="48" r="2"/><circle cx="32" cy="52" r="2"/><circle cx="42" cy="48" r="2"/>
            <circle cx="27" cy="55" r="1.4"/><circle cx="38" cy="46" r="1.4"/>
          </g>
        </svg>
      );
    case 'storm':
      return (
        <svg viewBox="0 0 64 64" style={s} aria-hidden="true">
          <defs><linearGradient id={u+'c'} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={cloudHi}/><stop offset="1" stopColor={cloudLo}/></linearGradient></defs>
          <g fill={`url(#${u}c)`}>
            <circle cx="22" cy="24" r="8"/><circle cx="32" cy="20" r="10"/><circle cx="44" cy="24" r="7"/>
            <rect x="14" y="24" width="36" height="12" rx="6"/>
          </g>
          <path d="M34 38 L26 50 L31 50 L28 60 L40 46 L34 46 Z" fill={bolt}/>
        </svg>
      );
    case 'fog':
      return (
        <svg viewBox="0 0 64 64" style={s} aria-hidden="true">
          <g stroke={fog} strokeWidth="3.5" strokeLinecap="round" fill="none">
            <line x1="10" y1="22" x2="48" y2="22" opacity=".4"/>
            <line x1="6"  y1="32" x2="54" y2="32" opacity=".6"/>
            <line x1="12" y1="42" x2="50" y2="42" opacity=".5"/>
            <line x1="18" y1="50" x2="46" y2="50" opacity=".4"/>
          </g>
        </svg>
      );
    default:
      return <svg viewBox="0 0 64 64" style={s} aria-hidden="true"><circle cx="32" cy="32" r="18" fill="none" stroke={color} strokeWidth="3"/></svg>;
  }
}

export const Glyph = {
  Search:   ({s=18, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 20 20" aria-hidden="true"><circle cx="9" cy="9" r="6" fill="none" stroke={c} strokeWidth="1.7"/><line x1="13.5" y1="13.5" x2="18" y2="18" stroke={c} strokeWidth="1.7" strokeLinecap="round"/></svg>,
  Plus:     ({s=18, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 20 20" aria-hidden="true"><line x1="10" y1="3" x2="10" y2="17" stroke={c} strokeWidth="1.8" strokeLinecap="round"/><line x1="3" y1="10" x2="17" y2="10" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  Chevron:  ({s=14, c='currentColor', dir='down'}) => <svg width={s} height={s} viewBox="0 0 14 14" aria-hidden="true" style={{transform: dir==='up'?'rotate(180deg)': dir==='left'?'rotate(90deg)':dir==='right'?'rotate(-90deg)':'none'}}><path d="M3 5 L7 9 L11 5" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  Refresh:  ({s=18, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke={c} strokeWidth="1.7" strokeLinecap="round"><path d="M3 10a7 7 0 0112-5l2 1"/><path d="M17 4v4h-4"/></svg>,
  Pin:      ({s=18, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke={c} strokeWidth="1.6"><path d="M10 17s-5-5-5-9a5 5 0 0110 0c0 4-5 9-5 9z"/><circle cx="10" cy="8" r="2"/></svg>,
  Sun:      ({s=14, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 14 14" aria-hidden="true" fill={c}><circle cx="7" cy="7" r="2.6"/><g stroke={c} strokeWidth="1.4" strokeLinecap="round"><line x1="7" y1="1.5" x2="7" y2="3"/><line x1="7" y1="11" x2="7" y2="12.5"/><line x1="1.5" y1="7" x2="3" y2="7"/><line x1="11" y1="7" x2="12.5" y2="7"/></g></svg>,
  Moon:     ({s=14, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 14 14" aria-hidden="true"><path d="M11 8a4.5 4.5 0 11-5-5 3.5 3.5 0 005 5z" fill={c}/></svg>,
  Layers:   ({s=18, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke={c} strokeWidth="1.5" strokeLinejoin="round"><path d="M10 3l7 4-7 4-7-4 7-4z"/><path d="M3 11l7 4 7-4"/><path d="M3 14l7 4 7-4"/></svg>,
  Bell:     ({s=18, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 14V9a5 5 0 1110 0v5l1.5 2H3.5L5 14z"/><path d="M8.5 18a1.5 1.5 0 003 0"/></svg>,
  Warn:     ({s=18, c='#fbbf24'}) => <svg width={s} height={s} viewBox="0 0 20 20" aria-hidden="true"><path d="M10 2 L19 18 L1 18 Z" fill={c}/><rect x="9.2" y="8" width="1.6" height="5" rx=".5" fill="#1f1100"/><circle cx="10" cy="15" r=".9" fill="#1f1100"/></svg>,
  Paw:      ({s=20, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden="true" fill={c}><ellipse cx="6" cy="9" rx="2" ry="2.6"/><ellipse cx="11" cy="6.5" rx="2" ry="2.8"/><ellipse cx="17" cy="9" rx="2" ry="2.6"/><ellipse cx="22" cy="13" rx="1.6" ry="2.2"/><path d="M12 11c-3 0-6 3-6 6 0 2 2 3 4 3 1 0 1.5-.6 2-.6s1 .6 2 .6c2 0 4-1 4-3 0-3-3-6-6-6z"/></svg>,
  FCC:      ({s=20, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h7"/><path d="M4 12h5"/><path d="M4 18h6"/><circle cx="17" cy="12" r="4"/></svg>,
  Wind:     ({s=14, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 14 14" aria-hidden="true" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round"><path d="M2 5h7a2 2 0 100-4"/><path d="M2 9h9a2 2 0 110 4"/></svg>,
  Drop:     ({s=14, c='currentColor'}) => <svg width={s} height={s} viewBox="0 0 14 14" aria-hidden="true"><path d="M7 1.5 C 7 1.5 11 6 11 9 a4 4 0 11-8 0 c0-3 4-7.5 4-7.5z" fill={c}/></svg>,
};
